import React, { useState } from 'react';
import { css } from 'glamor';
import { DateTime } from 'luxon';
import { useQuery } from '@apollo/client';

import { Loading, LoadingError } from '../../components/LoadingFork';
import { Table, THead, TBody, TR, TH, TD } from '../../components/Table';
import { Well } from '../../components/Well';
import { Button, HollowButton } from '../../components/Button';
import { StateDot } from '../../components/bayAccess/StateDot';

import { listParkingOperationsLog } from './queries';
import { styles } from './styles';

const PAGE_SIZE = 25;

const ACTION_LABEL = {
    out_of_service: 'Taken out of service',
    return_to_service: 'Returned to service',
    open: 'Barrier opened',
    close: 'Barrier closed',
    OPEN: 'Barrier opened',
    CLOSE: 'Barrier closed',
    IDENTIFY: 'Device identify',
};

const TYPE_FILTERS = [
    { value: 'all', label: 'All events' },
    { value: 'out_of_service', label: 'Taken out of service' },
    { value: 'return_to_service', label: 'Returned to service' },
    { value: 'OPEN', label: 'Barrier opened' },
    { value: 'CLOSE', label: 'Barrier closed' },
    { value: 'IDENTIFY', label: 'Device identify' },
];

const OUTCOME_FLAVOUR = {
    done: 'success',
    SUCCESS: 'success',
    BARRIER_BUSY: 'warning',
    JAMMED: 'danger',
};

const OUTCOME_LABEL = {
    done: 'Done',
    SUCCESS: 'Success',
    BARRIER_BUSY: 'Barrier busy',
    JAMMED: 'Jammed',
};

const EMPTY_FILTERS = { fromDate: '', toDate: '', type: 'all' };

function formatWhen(iso, tz) {
    return iso
        ? DateTime.fromISO(iso).setZone(tz || 'local').setLocale('en-GB').toFormat('d MMM yyyy, HH:mm ZZZZ')
        : '—';
}

function actionLabel(entry) {
    return ACTION_LABEL[entry.action] || entry.action;
}

function bayOf(entry, bayLabelById) {
    return (entry.parkingBayId && bayLabelById[entry.parkingBayId]) || '';
}

export function ActivityLog({ bayLabelById = {}, tz }) {
    const [draft, setDraft] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [newestFirst, setNewestFirst] = useState(true);
    const [offset, setOffset] = useState(0);

    const { data = {}, loading, error } = useQuery(listParkingOperationsLog, {
        variables: {
            limit: PAGE_SIZE,
            offset,
            action: applied.type === 'all' ? null : applied.type,
            from: applied.fromDate || null,
            to: applied.toDate || null,
            order: newestFirst ? 'newest' : 'oldest',
        },
        fetchPolicy: 'network-only',
        pollInterval: 60000,
    });

    const entries = data.parklioBayActionLog || [];
    const page = Math.floor(offset / PAGE_SIZE);
    const hasMore = entries.length === PAGE_SIZE;

    const setField = (name) => (value) => setDraft((prev) => ({ ...prev, [name]: value }));
    const apply = () => { setApplied(draft); setOffset(0); };
    const clear = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setOffset(0); };
    const toggleOrder = () => { setNewestFirst((value) => !value); setOffset(0); };
    const prev = () => setOffset((value) => Math.max(0, value - PAGE_SIZE));
    const next = () => setOffset((value) => value + PAGE_SIZE);

    const typeFilterLabel = (TYPE_FILTERS.find((option) => option.value === applied.type) || {}).label;
    const activeFilters = [
        applied.fromDate && `From: ${applied.fromDate}`,
        applied.toDate && `To: ${applied.toDate}`,
        applied.type !== 'all' && `Type: ${typeFilterLabel}`,
    ].filter(Boolean);

    return (
        <div>
            <div className={styles.nickHead}>
                <div className={styles.pageDescription}>Staff-action log</div>
                <HollowButton size="small" spaceBelow="none" onClick={toggleOrder}>
                    {newestFirst ? 'Newest first' : 'Oldest first'}
                </HollowButton>
            </div>

            <form className={styles.nickToolbar} onSubmit={(event) => { event.preventDefault(); apply(); }}>
                <div className={styles.nickField}>
                    <span className={styles.nickFieldLabel}>From</span>
                    <input type="date" className={styles.nickDateInput} value={draft.fromDate} onChange={(event) => setField('fromDate')(event.target.value)} />
                </div>
                <div className={styles.nickField}>
                    <span className={styles.nickFieldLabel}>To</span>
                    <input type="date" className={styles.nickDateInput} value={draft.toDate} onChange={(event) => setField('toDate')(event.target.value)} />
                </div>
                <div className={styles.nickField}>
                    <span className={styles.nickFieldLabel}>Type</span>
                    <select className={styles.filterSelect} value={draft.type} onChange={(event) => setField('type')(event.target.value)}>
                        {TYPE_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                </div>
                <Button size="small" spaceBelow="none" type="submit">Apply</Button>
                <HollowButton size="small" spaceBelow="none" type="button" onClick={clear}>Clear</HollowButton>
            </form>
            {activeFilters.length > 0 ? (
                <div className={css(styles.muted, { marginBottom: 12 })}>Active filters: {activeFilters.join(' · ')}</div>
            ) : null}

            {error ? <LoadingError error={error} /> : null}

            {loading && entries.length === 0 && offset === 0 ? (
                <Well><Loading /></Well>
            ) : entries.length === 0 ? (
                <>
                    <Well>{offset > 0 ? 'No further entries.' : (activeFilters.length === 0 ? 'No staff actions recorded yet.' : 'No audit events match these filters.')}</Well>
                    {offset > 0 ? (
                        <div className={styles.pager}>
                            <span>{`Page ${page + 1}`}</span>
                            <HollowButton size="small" spaceBelow="none" onClick={prev}>Previous</HollowButton>
                        </div>
                    ) : null}
                </>
            ) : (
                <>
                    <Table>
                        <THead>
                            <tr><TH>When</TH><TH>Action</TH><TH>Bay</TH><TH>Actor</TH><TH>Reason</TH><TH>Outcome</TH></tr>
                        </THead>
                        <TBody>
                            {entries.map((entry) => (
                                <TR key={entry.id}>
                                    <TD>
                                        <div className={styles.cellMain}>{formatWhen(entry.createdAt, tz)}</div>
                                        <div className={styles.cellSub}>{entry.id.slice(0, 8)}</div>
                                    </TD>
                                    <TD>{actionLabel(entry)}</TD>
                                    <TD>{bayOf(entry, bayLabelById) || '—'}</TD>
                                    <TD className={styles.cellSub}>{entry.performedByEmail}</TD>
                                    <TD className={styles.cellSub}>{entry.reason || '—'}</TD>
                                    <TD>{entry.outcome ? <StateDot flavour={OUTCOME_FLAVOUR[entry.outcome] || 'gray'}>{OUTCOME_LABEL[entry.outcome] || entry.outcome}</StateDot> : '—'}</TD>
                                </TR>
                            ))}
                        </TBody>
                    </Table>

                    <div className={styles.pager}>
                        <span>{`Page ${page + 1} · ${PAGE_SIZE} per page`}</span>
                        <span className={css({ display: 'flex', gap: 8 })}>
                            <HollowButton size="small" spaceBelow="none" disabled={offset === 0} onClick={prev}>Previous</HollowButton>
                            <HollowButton size="small" spaceBelow="none" disabled={!hasMore} onClick={next}>Next</HollowButton>
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
